import { useCallback, useMemo, useRef, useState } from 'react';
import { isEmpty, uniq } from 'lodash';

import useFabricFavs from 'data-hooks/useFabricFavs';
import usePDPOrderEdit from 'data-hooks/usePDPOrderEdit';
import useFlashSaleList from 'hooks/useFlashSaleList';
import usePDPOptionsClearanceData from 'data-hooks/useClearanceData/usePDPOptionsClearanceData';
import parseFabricMaterial from 'commons/parseFabricMaterial';

import {
  PDP_READYTOSHIP_TAB_ID,
  PDP_NAMED_FILTERS,
  GREENGUARD_FABRICS,
  PANTONE_FABRIC_CONFIG,
} from 'commons/constants';
import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import trimPopularMaterialsArray from './trimPopularMaterialsArray';
import moveMaterialToStart from './moveMaterialToStart';
import getUniqueMaterials from './getUniqueMaterials';

const { pantoneFilterSlug } = PANTONE_FABRIC_CONFIG;

const usePDPFabricFilters = (slug, isEditOrder) => {
  const {
    data: {
      options,
      productId,
      selectedOptions,
      selectedOptionsFromURLOverride,
      stock,
      upholsteryOptionId,
    } = {},
  } = usePDPOptions();

  const {
    data: { userFabricFavs: favMaterialsObj, fabricFavsInfo } = {},
  } = useFabricFavs({ loadMaterialDetails: true });

  const {
    products: flashSaleProductIds,
    variations: flashSaleVariations,
  } = useFlashSaleList();

  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();

  const [activeNamedFilters, setActiveNamedFilters] = useState([]);

  const resetNamedFilters = useCallback(() => {
    setActiveNamedFilters([]);
  }, []);

  const [activeColorFilters, setActiveColorFilters] = useState([]);

  const resetColorFilters = useCallback(() => {
    setActiveColorFilters([]);
  }, []);

  const clearAllFilters = useCallback(() => {
    resetNamedFilters();
    resetColorFilters();
  }, [resetColorFilters, resetNamedFilters]);

  // Toggle named upholstery filters such as Velvets, Safeguard
  const toggleNamedFilter = useCallback(filterId => {
    setActiveNamedFilters(prev =>
      prev?.includes(filterId)
        ? prev?.filter(v => v !== filterId)
        : [...prev, filterId]
    );
  }, []);

  const toggleColorFilter = useCallback(color => {
    setActiveColorFilters(prev => {
      const updatedActiveColorFilters = prev?.includes(color)
        ? prev?.filter(v => v !== color)
        : [...prev, color];

      return uniq(updatedActiveColorFilters);
    });
  }, []);

  const { data: orderEditData = {} } = usePDPOrderEdit({
    skip: !isEditOrder,
  });

  // for clearance materials
  const { data: productClearanceData } = usePDPOptionsClearanceData(productId);

  const editOrderDiscount = orderEditData?.cartItem?.discount_percent; // this can become problematic in some edge cases
  const formatOptionValue = useCallback(
    ({
      additional_cost: additionalCost,
      color,
      description,
      exclude_from_sale: excludeFromSale,
      group,
      id,
      image,
      is_active: isActive,
      performance_fabric: performanceFabric,
      pet_friendly: petFriendly,
      pantone_flag,
      popular,
      popularOrder,
      sku,
      sprite_config: spriteConfig,
      sustainable_fabric: sustainableFabric,
      tier,
      value: label,
    }) => {
      const groupName = group?.name || '';
      const materials = parseFabricMaterial(group?.material);
      const velvet = materials.includes('velvet');
      const greenguardFabric =
        materials.includes('greenguard') ||
        materials.includes('greenguard-gold') ||
        (groupName ? GREENGUARD_FABRICS.includes(groupName) : false);

      return {
        additionalCost:
          isEditOrder &&
          !excludeFromSale &&
          additionalCost > 0 &&
          editOrderDiscount > 0
            ? Math.round(additionalCost * ((100 - editOrderDiscount) / 100))
            : additionalCost,
        cleaningCode: group?.cleaning_code,
        color,
        description,
        exclude_from_sale: excludeFromSale,
        greenguard_fabric: greenguardFabric,
        groupMaterial: group?.material,
        groupName,
        image,
        is_active: isActive,
        label,
        materialId: id,
        origAddlCost: additionalCost,
        performance_fabric: performanceFabric,
        pet_friendly: petFriendly,
        pantone_flag,
        popular,
        popularOrder,
        sku,
        spriteConfig,
        sustainable_fabric: sustainableFabric,
        tier,
        upholsteryOptionId,
        velvet,
      };
    },
    [editOrderDiscount, isEditOrder, upholsteryOptionId]
  );

  // optimize calls
  const clearanceFabricsSKU = useMemo(
    () => Object.keys(productClearanceData?.optionsClearanceData || {}),
    [productClearanceData?.optionsClearanceData]
  );

  const isReadyToShipTab = pdpSelectedCustomizerTab === PDP_READYTOSHIP_TAB_ID;
  const finalOptions = isReadyToShipTab ? stock?.stockOptions : options;

  const upholsteryOption = finalOptions?.find(
    ({ id }) => id === upholsteryOptionId
  );

  const upholsteryOptionTypeName = upholsteryOption?.name || 'upholstery';

  const allMaterialsForCurrentTab = useMemo(() => {
    if (!upholsteryOption) {
      return [];
    }

    return (upholsteryOption?.values || [])
      .filter(
        optionValue =>
          (!isReadyToShipTab && optionValue?.is_active) || isReadyToShipTab
      )
      .map(optionValue => formatOptionValue(optionValue))
      .map(material => {
        if (material.pantone_flag === 1) {
          return {
            ...material,
            [pantoneFilterSlug]: true,
          };
        }

        return material;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [formatOptionValue, isReadyToShipTab, upholsteryOption]);

  const favoriteMaterials = useMemo(() => {
    if (!allMaterialsForCurrentTab) {
      return [];
    }

    const allFavoriteMaterials = allMaterialsForCurrentTab.filter(
      material => favMaterialsObj?.[material.materialId]
    );

    const favMaterialIds = allFavoriteMaterials.reduce(
      (acc, material) => ({ ...acc, [material.materialId]: true }),
      {}
    );

    const updatedFavoriteMaterials = [...allFavoriteMaterials];

    // CON-3009 - Get details of fav fabrics not applicable to product
    const favMaterialsNotApplicableToProduct = Object.keys(
      favMaterialsObj || {}
    ).filter(materialId => !favMaterialIds[materialId]);

    if (
      favMaterialsNotApplicableToProduct?.length > 0 &&
      !isEmpty(fabricFavsInfo)
    ) {
      favMaterialsNotApplicableToProduct.forEach(materialId => {
        const materialDetail = fabricFavsInfo[materialId];

        if (materialDetail) {
          updatedFavoriteMaterials.push({
            ...materialDetail,
            applicableForProduct: false,
            label: materialDetail.value?.trim(),
            groupMaterial: materialDetail.group?.material,
            cleaningCode: materialDetail.group?.cleaning_code,
            pet_friendly: materialDetail.group?.pet_friendly,
            pantone_flag: materialDetail.group?.pantone_flag,
            velvet: materialDetail.group?.material === 'velvet',
            greenguard_fabric:
              materialDetail.group?.material === 'greenguard' ||
              GREENGUARD_FABRICS.includes(materialDetail.group?.name),
            materialId: parseInt(materialId, 10),
          });
        }
      });
    }

    return updatedFavoriteMaterials;
  }, [allMaterialsForCurrentTab, fabricFavsInfo, favMaterialsObj]);

  const allInStockValues = useMemo(() => {
    if (!stock?.stockOptions?.length || !upholsteryOptionId) {
      return null;
    }

    // Find first in stock value
    const inStockOption = stock?.stockOptions.find(
      ({ id }) => id === upholsteryOptionId
    );

    const updatedAllInStockValues = inStockOption ? inStockOption.values : null;

    return updatedAllInStockValues;
  }, [stock?.stockOptions, upholsteryOptionId]);

  const instockOptionValueIds = useMemo(() => {
    const updatedInstockOptionValueIds = [];

    allInStockValues?.forEach(instockVal => {
      if (instockVal?.is_active) {
        updatedInstockOptionValueIds.push(instockVal.id);
      }
    });

    return updatedInstockOptionValueIds;
  }, [allInStockValues]);

  const inStockOptions = useMemo(() => {
    const updatedInStockOptions = allInStockValues
      ? allInStockValues
          .map(instockVal => {
            if (!instockVal?.is_active) {
              return null;
            }

            return {
              materialId: instockVal.id,
              label: instockVal.value,
              image: instockVal.image,
              spriteConfig: instockVal.sprite_config,
              upholsteryOptionId,
              description: instockVal.description,
              groupName: instockVal.group?.name || '',
              tier: instockVal.tier,
              color: instockVal.color,
              popular: instockVal.popular,
              pet_friendly: instockVal.pet_friendly,
              pantone_flag: instockVal.pantone_flag,
              exclude_from_sale: instockVal.exclude_from_sale,
              performance_fabric: instockVal.performance_fabric,
              sustainable_fabric: instockVal.sustainable_fabric,
              sku: instockVal.sku,
              additionalCost:
                isEditOrder &&
                !instockVal.exclude_from_sale &&
                instockVal.additional_cost > 0 &&
                editOrderDiscount > 0
                  ? Math.round(
                      instockVal.additional_cost *
                        ((100 - editOrderDiscount) / 100)
                    )
                  : instockVal.additional_cost,
              groupMaterial: instockVal.group?.material,
              cleaningCode: instockVal.group?.cleaning_code,
              origAddlCost: instockVal.additional_cost,
            };
          })
          .filter(v => !!v)
      : null;

    return updatedInStockOptions;
  }, [allInStockValues, editOrderDiscount, isEditOrder, upholsteryOptionId]);

  // Find selected material
  const selectedMaterial = useMemo(() => {
    if (!selectedOptions) return {};

    const selectedMaterialId = selectedOptions[upholsteryOptionId];

    const updatedSelectedMaterial = allMaterialsForCurrentTab.find(
      ({ materialId }) => materialId === selectedMaterialId
    );

    return updatedSelectedMaterial || {};
  }, [allMaterialsForCurrentTab, selectedOptions, upholsteryOptionId]);

  // Set the initial selected material using useRef so that it is only set
  // during first render.
  const initialSelectedMaterialRef = useRef(selectedMaterial);

  const isProductInFlashSale = flashSaleProductIds?.includes(productId);

  const flashSaleVariation = useMemo(
    () => (isProductInFlashSale ? flashSaleVariations?.[productId] : {}),
    [flashSaleVariations, isProductInFlashSale, productId]
  );

  const flashSaleMaterials = useMemo(
    () =>
      isProductInFlashSale
        ? allMaterialsForCurrentTab.filter(val =>
            flashSaleVariation[upholsteryOptionId]?.includes(val.materialId)
          )
        : [],
    [
      allMaterialsForCurrentTab,
      flashSaleVariation,
      isProductInFlashSale,
      upholsteryOptionId,
    ]
  );

  // Derive all materials marked as popular in API response
  const allPopularMaterials = useMemo(() => {
    // Find all materials marked as popular that are not part of the flash
    // sale, and sort them by popularOrder
    const allPopularMaterialsInOrder = allMaterialsForCurrentTab
      .filter(
        material =>
          material.popular &&
          !flashSaleVariation[upholsteryOptionId]?.includes(material.materialId)
      )
      .sort((a, b) => a.popularOrder - b.popularOrder);

    // If there are no materials marked as popular, then use all materials
    return allPopularMaterialsInOrder.length
      ? allPopularMaterialsInOrder
      : allMaterialsForCurrentTab;
  }, [allMaterialsForCurrentTab, flashSaleVariation, upholsteryOptionId]);

  // Show any flash sale materials first in the popular materials group
  const popularMaterialsFlashSalesFirst = useMemo(() => {
    const allPopularMaterialsWithFlashSales = [
      ...flashSaleMaterials,
      ...allPopularMaterials,
    ];

    return trimPopularMaterialsArray(allPopularMaterialsWithFlashSales);
  }, [allPopularMaterials, flashSaleMaterials]);

  // Show flash sales and in stock materials first in the popular group
  const popularMaterialsFlashSalesAndInStockFirst = useMemo(() => {
    const allStockOptionsPresent =
      !!inStockOptions?.length &&
      !selectedOptionsFromURLOverride &&
      !!inStockOptions.every(
        (stockedOption, idx) =>
          popularMaterialsFlashSalesFirst?.[idx] === stockedOption.materialId
      );

    if (inStockOptions?.length && !selectedOptionsFromURLOverride) {
      if (allStockOptionsPresent) {
        return popularMaterialsFlashSalesFirst;
      }

      const flashSaleInStockAndPopularMaterials = [
        ...(flashSaleMaterials || []),
        ...inStockOptions.filter(
          inStockVal =>
            !(flashSaleVariation[upholsteryOptionId] || []).includes(
              inStockVal.materialId
            )
        ),
        ...popularMaterialsFlashSalesFirst.filter(
          val =>
            !instockOptionValueIds.includes(val.materialId) &&
            !(flashSaleVariation[upholsteryOptionId] || []).includes(
              val.materialId
            )
        ),
      ];

      return trimPopularMaterialsArray(flashSaleInStockAndPopularMaterials);
    }

    return popularMaterialsFlashSalesFirst;
  }, [
    flashSaleMaterials,
    flashSaleVariation,
    inStockOptions,
    instockOptionValueIds,
    popularMaterialsFlashSalesFirst,
    selectedOptionsFromURLOverride,
    upholsteryOptionId,
  ]);

  // Derive final popular materials array
  const popularMaterials = useMemo(() => {
    const initialMaterialFirst = moveMaterialToStart(
      popularMaterialsFlashSalesAndInStockFirst,
      !activeNamedFilters.length && !isReadyToShipTab
        ? initialSelectedMaterialRef.current
        : null
    );

    const trimmedInitialMaterialFirst = trimPopularMaterialsArray(
      initialMaterialFirst
    );

    return trimmedInitialMaterialFirst;
  }, [
    activeNamedFilters,
    isReadyToShipTab,
    popularMaterialsFlashSalesAndInStockFirst,
  ]);

  const namedFilterConditions = useMemo(
    () => ({
      [PDP_NAMED_FILTERS.FAVORITES]: material =>
        favoriteMaterials.includes(material),
      [PDP_NAMED_FILTERS.VELVET]: material => !!material.velvet,
      [PDP_NAMED_FILTERS.GREENGUARD]: material => !!material.greenguard_fabric,
      [PDP_NAMED_FILTERS.PETFRIENDLY]: material => !!material.pet_friendly,
      [PDP_NAMED_FILTERS.PANTONE]: material => !!material.pantone_flag,
      [PDP_NAMED_FILTERS.SAFEGUARD]: material => !!material.performance_fabric,
      [PDP_NAMED_FILTERS.SUSTAINABLE]: material =>
        !!material.sustainable_fabric,
      [PDP_NAMED_FILTERS.CLEARANCE]: material =>
        clearanceFabricsSKU.includes(material.sku),
    }),
    [clearanceFabricsSKU, favoriteMaterials]
  );

  const isLeatherProduct = upholsteryOptionTypeName === 'Leather';
  const enableNamedFilters = !isReadyToShipTab && !isLeatherProduct;
  const enableColorFilters = !isLeatherProduct;

  // update filtered materials
  const filteredMaterials = useMemo(() => {
    const isInitialMaterialInAllMaterials = !!allMaterialsForCurrentTab.find(
      material =>
        material.materialId === initialSelectedMaterialRef.current?.materialId
    );

    // If the material is leather, return all
    if (isLeatherProduct) {
      return isInitialMaterialInAllMaterials
        ? getUniqueMaterials([
            initialSelectedMaterialRef.current,
            ...allMaterialsForCurrentTab,
          ])
        : allMaterialsForCurrentTab;
    }

    const initialPopularAndAllMaterials = getUniqueMaterials([
      isInitialMaterialInAllMaterials && !activeNamedFilters.length
        ? initialSelectedMaterialRef.current
        : null,
      ...popularMaterials,
      ...allMaterialsForCurrentTab,
    ]);

    return initialPopularAndAllMaterials.filter(material => {
      if (
        enableColorFilters &&
        activeColorFilters.length &&
        (!material.color ||
          !activeColorFilters.includes(material.color.toLowerCase()))
      ) {
        return false;
      }

      if (
        enableNamedFilters &&
        activeNamedFilters.length &&
        !activeNamedFilters.every(namedFilter =>
          namedFilterConditions[namedFilter]?.(material)
        )
      ) {
        return false;
      }
      return true;
    });
  }, [
    activeColorFilters,
    activeNamedFilters,
    allMaterialsForCurrentTab,
    enableColorFilters,
    enableNamedFilters,
    isLeatherProduct,
    namedFilterConditions,
    popularMaterials,
  ]);

  // derive disabled materials
  const disabledMaterialIds = useMemo(() => {
    const { stockOptionsDisabledStatus } = stock || {};

    return isReadyToShipTab && allMaterialsForCurrentTab
      ? allMaterialsForCurrentTab
          .filter(material => stockOptionsDisabledStatus?.[material.materialId])
          .map(material => material.materialId)
      : [];
  }, [allMaterialsForCurrentTab, isReadyToShipTab, stock]);

  const filterCounts = useMemo(
    () => ({
      [PDP_NAMED_FILTERS.PETFRIENDLY]:
        allMaterialsForCurrentTab?.filter(material => material.pet_friendly)
          ?.length || 0,
      [PDP_NAMED_FILTERS.PANTONE]:
        allMaterialsForCurrentTab?.filter(material => material.pantone_flag)
          ?.length || 0,
      [PDP_NAMED_FILTERS.SAFEGUARD]:
        allMaterialsForCurrentTab?.filter(
          material => material.performance_fabric
        )?.length || 0,
      [PDP_NAMED_FILTERS.SUSTAINABLE]:
        allMaterialsForCurrentTab?.filter(
          material => material.sustainable_fabric
        )?.length || 0,
      [PDP_NAMED_FILTERS.VELVET]:
        allMaterialsForCurrentTab?.filter(material => material.velvet)
          ?.length || 0,
      [PDP_NAMED_FILTERS.FAVORITES]:
        Object.keys(favMaterialsObj || {}).length || 0,
      [PDP_NAMED_FILTERS.GREENGUARD]:
        allMaterialsForCurrentTab?.filter(
          material =>
            material.groupMaterial === 'greenguard' ||
            GREENGUARD_FABRICS.includes(material.groupName)
        )?.length || 0,
    }),
    [allMaterialsForCurrentTab, favMaterialsObj]
  );

  const flashSaleOptionIds = useMemo(
    () => flashSaleMaterials.map(material => material.materialId),
    [flashSaleMaterials]
  );

  return {
    data: {
      activeColorFilters,
      activeNamedFilters,
      allMaterials: allMaterialsForCurrentTab,
      allMaterialsLength: allMaterialsForCurrentTab.length,
      disabledMaterialIds,
      favMaterials: favMaterialsObj,
      filterCounts,
      filteredMaterials,
      flashSaleOptionIds,
      isClearanceFabricAvailable: clearanceFabricsSKU?.length > 0,
      isSelectedOptionsQuickship: stock?.isSelectedOptionsQuickship,
      optionName: upholsteryOptionTypeName,
      selectedMaterial,
      selectedOptionsStockQty: stock?.selectedOptionsStockQty,
      selectionValidity: stock?.selectionValidity,
      upholsteryOptionId,
    },
    actions: {
      clearAllFilters,
      toggleColorFilter,
      toggleNamedFilter,
    },
  };
};

export default usePDPFabricFilters;
